import { ApiRequest, BaseResource, postItem } from "utils/ApiUtils";
import buildQuery from 'odata-query';
import ODataUtil from './ODataUtil';


const get_issues_endpoint = "/Issue";
const get_status_endpoint = "/Domain?domainName=LuIssueProcessingState&includeReserved=false"

// A missing or unparseable CommentDate sorts to the end rather than scrambling the rest.
const commentTime = (comment) => {
  const time = new Date(comment && comment.CommentDate).getTime()
  return isNaN(time) ? 0 : time
}

// Newest comment first. The API returns them in no particular order, and this feeds both the
// grid cell and the exported file, so they stay in step.
const issueCommentText = (rowData) => {
  const comments = rowData && rowData.IssueComment
  if (!Array.isArray(comments)) return ""
  return [...comments]
    .sort((a, b) => commentTime(b) - commentTime(a))
    .map(c => c && c.Comment).filter(Boolean).join("\n")
}

class Issues extends BaseResource {
  constructor(args, attr = Issues.attributes) {
    super(args, attr);
  };

  static columns = [
    { title: "Open Date", field: "OpenDate", type: "date" },
    { title: "Last Updated Date", field: "OpenDate", type: "date", defaultSort: "desc" },
    { title: "IssueId", field: "IssueId", hidden: true },
    { title: "WorkItem #", field: "WorkItemNumber_WorkItem" },
    { title: "Created By", field: "MadeBy", width: "1%" },
    { title: "Issue #", field: "IssueNumber" },
    // CSV/PDF only: hidden in the grid, but export:true keeps it in the exported file, where each
    // revision of a thread is its own row.
    { title: "Revision", field: "Revision", hidden: true, export: true },
    { title: "Status", field: "Description_IssueProcStatus" },
    { title: "Inspection #", field: "InspectionNumber_Inspection" },
     { title: "Inspector", field: "Name_SystemUser" },
    { title: "Ihwi Id", field: "IhwiId", hidden: true },
    { title: "IssueTypeId", field: "IssueTypeId", hidden: true },
    { title: "IssueProcStatusId", field: "IssueProcStatusId_IssueProcStatus", hidden: true },
    //{ title: "Require Res?", field: "ResolutionReqYn", type: "boolean" },
    { title: "Type", field: "Description_IssueType" },
    { title: "Description", field: "IssueDescription" },
    {
      title: "IssueComment", field: "IssueComment", hidden: true,
      render: issueCommentText,
      cellStyle: { whiteSpace: "pre-line"}
    },
    { title: "Attachment", field: "Attachment", hidden: true },
  ]

  static columnsStatus = [
    { title: "IssueProcStatusId", field: "domainId" },
    { title: "Description", field: "description" },

  ]

  static expand = [
    {
      "Ihwi": {
        "expand": [{ "WorkItem": { "select": ["ProjectId", "WorkItemNumber"] } }, {
          "Inspection": {
            select: ["InspectionNumber"]
          }
        },
        {
          "Inspection": {
            expand:
            {
              "Rassignment": {
                expand:
                {
                  "SystemUser": { "select": ["Name"] }
                }
              }
            }
          }
        }]
      }
    },
    { "IssueProcStatus": { "select": ["IssueProcStatusId", "Description"] } },
    { "IssueType": { "select": ["IssueTypeId", "Description"] } },
    { "IssueComment": {} }

  ]

  static attributes = Issues.columns.map(r => r.field);
  static columnsStatusAttr = Issues.columnsStatus.map(r => r.field)
};

// Walks a root down its first-child chain, returning [root, ..., leaf]. A status update creates
// a new child record rather than editing in place, so that chain is the issue's revision history
// and the leaf is its current state.
function getChain(root) {
  const chain = [root];
  let node = root;
  while (node.children.length > 0) {
    node = node.children[0];
    chain.push(node);
  }
  return chain;
}

async function convert_issues_to_table_schema({ "value": items, "@odata.count": count } = {}, status, skip = 0, pageSize = 10, includeHistory = false) {
  var map = {}, node, roots = [], i;
  for (i = 0; i < items.length; i += 1) {
    map[items[i].IssueId] = i; // initialize the map
    items[i].children = []; // initialize the children
  }

  for (i = 0; i < items.length; i += 1) {
    node = items[i];
    if (node.ParentIssueId !== "0" && node.ParentIssueId !== null) {
      if (items[map[node.ParentIssueId]] !== undefined) {
        items[map[node.ParentIssueId]].children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  }

  // Helper function to find the root issue's OpenDate for any issue
  function findRootOpenDate(issue) {
    let current = issue;
    while (current.ParentIssueId !== "0" && current.ParentIssueId !== null) {
      const parentIndex = map[current.ParentIssueId];
      if (parentIndex !== undefined && items[parentIndex]) {
        current = items[parentIndex];
      } else {
        break;
      }
    }
    return current.OpenDate;
  }

  // One thread per root: the whole revision chain, plus the collapsed row the grid shows for it
  // (built from the leaf, but carrying the root's open date).
  const threads = roots.map(root => {
    const chain = getChain(root);
    const leafRow = Issues.constructWithExpand(chain[chain.length - 1]);
    leafRow.OpenDate = root.OpenDate;
    return { chain, leafRow };
  });

  // Status is judged on the thread's latest revision, so a thread is kept or dropped as a whole.
  // Filtering revision rows individually would strip the Open/Initial ancestors out of a thread
  // that has since been resolved.
  const visible = threads.filter(({ leafRow: obj }) => status === "Open" ? obj.Description_IssueProcStatus === "Open" || obj.Description_IssueProcStatus === "Initial" || obj.Description_IssueProcStatus === "Follow-Up" || obj.Description_IssueProcStatus === "In Progress" : obj.Description_IssueProcStatus === "Closed" || obj.Description_IssueProcStatus === "Resolved" || obj.Description_IssueProcStatus === "Closed - Unresolved");

  // The grid gets one row per thread. The CSV export asks for includeHistory and gets one row per
  // revision instead, oldest first, each keeping its own dates rather than the root's.
  let filtered_data = includeHistory
    ? visible.reduce((rows, { chain }) => rows.concat(
        chain.map((revision, index) => {
          const row = Issues.constructWithExpand(revision);
          row.Revision = `${index + 1} of ${chain.length}`;
          return row;
        })
      ), [])
    : visible.map(({ leafRow }) => leafRow);
  count = filtered_data.length;

  // Create allData with OpenDate for each item
  const allData = items.map(r => {
    const issue = Issues.constructWithExpand(r);
    issue.OpenDate = findRootOpenDate(r);
    return issue;
  });

  return {
    data: filtered_data, count, roots, map, items, allData
  };
};

async function getURL(issueid) {
  const urlparams = "?issueid=" + issueid
  const issuesAttachments = await ApiRequest(`${get_issues_endpoint}/Urls`, urlparams);
  return issuesAttachments
}



// Orders rows by their concatenated comment text. Used in place of an OData $orderby, which
// the API can't do for a collection navigation property.
function sortByIssueComment(rows, orderByDirection) {
  const direction = orderByDirection === "desc" ? -1 : 1;
  return [...rows].sort((a, b) =>
    direction * issueCommentText(a).localeCompare(issueCommentText(b)));
}

async function getIssues(page, per_page, orderByInfo, orderByDirection, search, filters, customFilter, includeHistory = false) {
  // IssueComment can't be ordered by the API, so keep it out of the query and sort the rows
  // afterwards. That is a sort over the full result set, not just a page, because the query
  // below sends no $top/$skip and brings back every matching issue.
  const sortOnComments = !!orderByInfo && orderByInfo.field === "IssueComment";
  const { skip, count, expand, orderBy, mappedFilter } = ODataUtil.UtilityBuilderNestedv3(
    per_page, page, Issues.expand, sortOnComments ? undefined : orderByInfo, orderByDirection, filters)
  const isDefaults = filters.find(r => r.defaults);
  const filter = {};
  if (isDefaults) {
    const defaults = isDefaults.defaults;
    if (defaults.projectId) filter["Ihwi/WorkItem/ProjectId"] = { eq: { type: 'guid', value: defaults.projectId } };
    if (customFilter && customFilter.InspectionNumber_Inspection) filter["Ihwi/Inspection/InspectionNumber"] = customFilter.InspectionNumber_Inspection;
  };
  const allFilters = mappedFilter.reduce((a, b) => ({ ...a, ...b }), filter);

  const params = buildQuery({ count, orderBy, expand, filter: allFilters });
  const data = await ApiRequest(get_issues_endpoint, params);
  const result = await convert_issues_to_table_schema(data, search.status, skip, undefined, includeHistory);
  // Skipped for history exports: that sort is a flat re-order of every row, which would split a
  // thread's revisions apart and leave the "n of m" rows scattered through the file.
  if (sortOnComments && !includeHistory) {
    result.data = sortByIssueComment(result.data, orderByDirection);
  };
  return result;
}

async function getIssueStatus() {
  const params = buildQuery({});
  const resp = await ApiRequest(get_status_endpoint, params);
  const data = resp.data.map(r => {
    let status = new Issues(r, Issues.columnsStatusAttr)
    return status
  })
  return {
    data
  };
}


async function getIssuesByID(page, per_page, orderByInfo, orderByDirection, search, projectId, status, filters) {

  const { skip, count, expand, orderBy, mappedFilter } = ODataUtil.UtilityBuilderNestedv3(
    per_page, page, Issues.expand, orderByInfo, orderByDirection, filters)

  const isDefaults = filters && filters.find(r => r.defaults);
  const filter = {};
  if (isDefaults) {
    const defaults = isDefaults.defaults;
    if (defaults.projectId) filter["Ihwi/WorkItem/ProjectId"] = { eq: { type: 'guid', value: defaults.projectId } };
    if (defaults.inspectionNumber) filter["Ihwi/Inspection/InspectionNumber"] = defaults.inspectionNumber;
    console.log(defaults.inspectionNumber)
  };
  const allFilters = mappedFilter.reduce((a, b) => ({ ...a, ...b }), filter);

  const params = buildQuery({ count, skip, orderBy, expand, filter: allFilters });
  const data = await ApiRequest(get_issues_endpoint, params);
  return convert_issues_to_table_schema(data, search.status);
}

async function addIssueComment(data, issueId) {
  const resourcePath = `${get_issues_endpoint}/Comment/Add`
  const params = `?issuetId=${issueId}`;
  const result = await postItem(resourcePath, params, data[0])
  return result;
}

async function addNewChildIssue(data, parentIssueID, statusIssueDesc, statusIssue, receiveDate, receiveComment, issueDescription) {
  data.ParentIssueId = parentIssueID
  data.IssueProcStatusId = statusIssue
  data.IssueDescription = issueDescription
  // data.IhwiId = data.IhwiId
  // data.IssueProcStatus = JSON.stringify(data.IssueProcStatus)
  // data.IssueTypeId = data.IssueTypeId
  data.ResolvedDate = ""
  data.ResolvedDescription = ""
  if (statusIssueDesc.search("Resolved") !== -1 || statusIssueDesc.search("Close") !== -1) {
    data.ResolvedDate = receiveDate
    data.ResolvedDescription = receiveComment
  };

  const resourcePath = `${get_issues_endpoint}/Add`
  const params = `?issuetId=${parentIssueID}`;
  const result = await postItem(resourcePath, params, data)
  return result;
}

export { Issues, getIssues, getIssuesByID, getIssueStatus, addIssueComment, addNewChildIssue, getURL };